import { useCallback, useEffect, useState } from "react";
import { useParams } from "react-router-dom";
import { toast } from "sonner";
import { Plus, AlarmClock, Search, GripVertical } from "lucide-react";
import api, { errMsg } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { phpShort, php, date } from "@/lib/format";
import { PageHeader, PeopleSelect, DemoTag } from "@/components/common/ui";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import LeadFormDialog from "@/components/leads/LeadFormDialog";
import LeadDrawer from "@/components/leads/LeadDrawer";
import StageMoveDialog, { moveLead } from "@/components/leads/StageMoveDialog";

function Card({ lead, draggable, onOpen }) {
  return (
    <div
      draggable={draggable}
      onDragStart={(e) => e.dataTransfer.setData("text/plain", lead.id)}
      onClick={() => onOpen(lead.id)}
      className={`group bg-white border rounded p-3 cursor-pointer transition-shadow hover:shadow-md ${lead.overdue ? "border-red-300" : "border-slate-200"}`}
      data-testid={`kanban-card-${lead.id}`}
    >
      <div className="flex items-start justify-between gap-2">
        <div className="min-w-0">
          <div className="text-sm font-semibold truncate">{lead.company || lead.name}<DemoTag show={lead.is_demo} /></div>
          {lead.company && <div className="text-xs text-slate-500 truncate">{lead.name}</div>}
        </div>
        {draggable && <GripVertical className="h-4 w-4 text-slate-300 shrink-0" />}
      </div>
      <div className="flex items-center justify-between mt-2 text-xs">
        <span className="num font-semibold text-[#0B192C]" title={php(lead.status === "won" ? lead.won_value : lead.value)}>{phpShort(lead.status === "won" ? lead.won_value : lead.value)}</span>
        <span className="text-slate-400 truncate max-w-[110px]">{lead.assigned_name || "Unassigned"}</span>
      </div>
      {lead.next_follow_up && (
        <div className={`mt-2 flex items-center gap-1 text-[11px] ${lead.overdue ? "text-red-600 font-semibold" : "text-slate-500"}`} data-testid={lead.overdue ? `overdue-badge-${lead.id}` : undefined}>
          <AlarmClock className="h-3 w-3" />{lead.overdue ? "Overdue · " : ""}{date(lead.next_follow_up)}
        </div>
      )}
      {lead.status === "lost" && lead.lost_reason && <div className="mt-1 text-[11px] text-red-500">{lead.lost_reason}</div>}
    </div>
  );
}

export default function Funnel() {
  const { market } = useParams();
  const { can, user } = useAuth();
  const [data, setData] = useState(null);
  const [q, setQ] = useState("");
  const [assigned, setAssigned] = useState("");
  const [create, setCreate] = useState(false);
  const [openId, setOpenId] = useState(null);
  const [move, setMove] = useState(null);
  const [over, setOver] = useState(null);

  const load = useCallback(async () => {
    try {
      const { data } = await api.get(`/funnels/${market}`, { params: { q: q || undefined, assigned_to: assigned || undefined } });
      setData(data);
    } catch (e) {
      toast.error(errMsg(e));
    }
  }, [market, q, assigned]);
  useEffect(() => { const t = setTimeout(load, 250); return () => clearTimeout(t); }, [load]);

  const canDrag = (lead) => can("leads.move_stage") && (can("leads.edit") || (can("leads.edit_own") && lead.assigned_to === user.id));

  const drop = async (stage, e) => {
    e.preventDefault();
    setOver(null);
    const lead = data.leads.find((l) => l.id === e.dataTransfer.getData("text/plain"));
    if (!lead || lead.stage_id === stage.id || !canDrag(lead)) return;
    if (stage.type !== "open") return setMove({ lead, stage });
    try {
      await moveLead(lead, stage);
      toast.success(`Moved to ${stage.name}`);
      load();
    } catch (err) {
      toast.error(errMsg(err));
    }
  };

  if (!data) return <div className="text-slate-400">Loading funnel…</div>;
  const openValue = data.leads.filter((l) => l.status === "open").reduce((a, l) => a + (l.value || 0), 0);
  const wonValue = data.leads.filter((l) => l.status === "won").reduce((a, l) => a + (l.won_value || 0), 0);

  return (
    <div data-testid={`funnel-page-${market}`}>
      <PageHeader eyebrow="Sales funnel" title={data.market.name} subtitle={`${data.leads.length} records · Open pipeline ${php(openValue)} · Won ${php(wonValue)}. Drag cards between stages to update.`}>
        <div className="relative"><Search className="h-4 w-4 absolute left-2.5 top-2.5 text-slate-400" /><Input placeholder="Search…" className="pl-8 w-48 bg-white" value={q} onChange={(e) => setQ(e.target.value)} data-testid="funnel-search-input" /></div>
        {can("leads.view_all") && <PeopleSelect value={assigned} onChange={setAssigned} includeUnassigned />}
        {can("leads.create") && <Button onClick={() => setCreate(true)} className="bg-orange-500 hover:bg-orange-600" data-testid="funnel-add-lead-button"><Plus className="h-4 w-4 mr-1" />New lead</Button>}
      </PageHeader>

      <div className="kanban-scroll flex gap-3 overflow-x-auto pb-4 -mx-4 px-4 sm:mx-0 sm:px-0">
        {data.stages.map((s) => {
          const items = data.leads.filter((l) => l.stage_id === s.id);
          const total = items.reduce((a, l) => a + ((s.type === "won" ? l.won_value : l.value) || 0), 0);
          const head = s.type === "won" ? "border-t-emerald-500" : s.type === "lost" ? "border-t-red-400" : "border-t-[#0B192C]";
          return (
            <div key={s.id}
              onDragOver={(e) => { e.preventDefault(); setOver(s.id); }}
              onDragLeave={() => setOver(null)}
              onDrop={(e) => drop(s, e)}
              className={`shrink-0 w-[272px] rounded-md border-t-[3px] ${head} ${over === s.id ? "bg-orange-50 ring-2 ring-orange-300" : "bg-slate-200/50"} transition-colors`}
              data-testid={`kanban-stage-${s.id}`}
            >
              <div className="px-3 pt-3 pb-2">
                <div className="flex items-center justify-between">
                  <span className="text-sm font-semibold text-[#0B192C]">{s.name}</span>
                  <span className="num text-xs bg-white rounded px-1.5 py-0.5" data-testid={`kanban-stage-count-${s.id}`}>{items.length}</span>
                </div>
                <div className="num text-xs text-slate-500 mt-0.5">{php(total)}</div>
              </div>
              <div className="px-2 pb-3 space-y-2 min-h-[120px] max-h-[65vh] overflow-y-auto">
                {items.map((l) => <Card key={l.id} lead={l} draggable={canDrag(l)} onOpen={setOpenId} />)}
              </div>
            </div>
          );
        })}
      </div>
      <p className="text-xs text-slate-400 mt-2">On touch devices, tap a card and use the stage selector to move it.</p>

      <LeadFormDialog open={create} onOpenChange={setCreate} market={market} onSaved={load} />
      <LeadDrawer leadId={openId} onClose={() => setOpenId(null)} onChanged={load} />
      <StageMoveDialog move={move} onClose={() => setMove(null)} onMoved={load} />
    </div>
  );
}
